import { queryOptions, useQuery } from "@tanstack/react-query";
import prettyBytes from "pretty-bytes";

import { AvailableRoles } from "@/apiClient";
import type { MakoAPIClient, User } from "@/apiClient";
import type { StatCardTone } from "@/components/cards/statCard";
import { userHasRole } from "@/components/navConfig";

import useApiClient from "./useApiClient";

const ASSET_STATS_QUERY_KEY = ["home-asset-statistics"];
const DIRECTORY_SUMMARY_QUERY_KEY = ["home-directory-summary"];

// throwOnError: false — each card surfaces its own error state so one failed
// query degrades that card alone rather than crashing the whole route.
const assetStatsQueryOptions = (api: MakoAPIClient) =>
  queryOptions({
    queryKey: ASSET_STATS_QUERY_KEY,
    // Aggregate over every indexed asset (no search filters supplied).
    queryFn: () => api.dataAccess.getSearchStatistics({}),
    throwOnError: false,
    refetchOnWindowFocus: false,
  });

const directorySummaryQueryOptions = (api: MakoAPIClient) =>
  queryOptions({
    queryKey: DIRECTORY_SUMMARY_QUERY_KEY,
    queryFn: () => api.dataAccess.getDirectoryInventorySummary(),
    throwOnError: false,
    refetchOnWindowFocus: false,
  });

export interface HomeMetric {
  key: string;
  label: string;
  value?: string;
  detail: string;
  tone: StatCardTone;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Loads the live aggregate data behind the homepage KPI cards. Each metric
 * carries its own loading / error flags so the cards can render independently.
 * Documentation metrics are only included for users authorized to read the
 * directory inventory summary.
 */
export default function useHomeDashboard(user: User | null): {
  metrics: HomeMetric[];
} {
  const api = useApiClient();
  // The directory inventory summary endpoint is only authorized for directory
  // curators, so its cards are only queried and shown for users with the role.
  const showDirectoryMetrics = userHasRole(
    user,
    AvailableRoles.REAL_ASSET_CREATOR
  );

  const assetStatsQuery = useQuery(assetStatsQueryOptions(api));
  const directorySummaryQuery = useQuery({
    ...directorySummaryQueryOptions(api),
    enabled: showDirectoryMetrics,
  });

  const summary = directorySummaryQuery.data;
  const totalConcrete = summary?.total_concrete_directories ?? 0;
  const documentedConcrete = summary?.documented_concrete_directories ?? 0;
  const coveragePercent =
    totalConcrete > 0
      ? Math.round((documentedConcrete / totalConcrete) * 100)
      : 0;

  const metrics: HomeMetric[] = [
    {
      key: "total-assets",
      label: "Total Data Assets",
      value: assetStatsQuery.data?.number_of_assets.toLocaleString(),
      detail: "Files indexed across all cruises",
      tone: "info",
      isLoading: assetStatsQuery.isPending,
      isError: assetStatsQuery.isError,
    },
    {
      key: "total-volume",
      label: "Total Data Volume",
      value:
        assetStatsQuery.data != null
          ? prettyBytes(assetStatsQuery.data.total_data_volume)
          : undefined,
      detail: "Total catalogued data size",
      tone: "info",
      isLoading: assetStatsQuery.isPending,
      isError: assetStatsQuery.isError,
    },
  ];

  if (showDirectoryMetrics) {
    metrics.push(
      {
        key: "awaiting-documentation",
        label: "Awaiting Documentation",
        value: summary?.awaiting_documentation_count?.toLocaleString(),
        detail: "Directory patterns needing documentation",
        tone: "caution",
        isLoading: directorySummaryQuery.isPending,
        isError: directorySummaryQuery.isError,
      },
      {
        key: "documentation-coverage",
        label: "Documentation Coverage",
        value: summary != null ? `${coveragePercent}%` : undefined,
        detail: `${documentedConcrete.toLocaleString()} of ${totalConcrete.toLocaleString()} directories documented`,
        tone: coveragePercent >= 80 ? "positive" : "caution",
        isLoading: directorySummaryQuery.isPending,
        isError: directorySummaryQuery.isError,
      }
    );
  }

  return { metrics };
}
